import React, { useState } from 'react'
import clsx from 'clsx'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import CatalogAdminPanel from './CatalogAdminPanel'
import PassSequenceImagesAdmin from './PassSequenceImagesAdmin'
import PassNameMappingsAdmin from './PassNameMappingsAdmin'
import BevelGeometryImagesAdmin from './BevelGeometryImagesAdmin'
import { useUnitSetting } from '../../utils/userUnitSetting'

type SettingsTab =
  | 'general'
  | 'standards'
  | 'access'
  | 'catalog'
  | 'passSequence'
  | 'passNameMappings'
  | 'bevelGeometry'

export default function Settings() {
  const [tab, setTab] = useState<SettingsTab>('general')
  const [unit, setUnit] = useUnitSetting()

  const tabs = [
    { id: 'general' as const, label: 'General' },
    { id: 'standards' as const, label: 'Standards & rules' },
    { id: 'access' as const, label: 'Access control' },
    { id: 'catalog' as const, label: 'Dropdown Catalog' },
    { id: 'passSequence' as const, label: 'Pass Sequence Images' },
    { id: 'passNameMappings' as const, label: 'Pass Name Mappings' },
    { id: 'bevelGeometry' as const, label: 'Bevel Geometry Images' }
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={clsx(
              'rounded-md px-3 py-2 text-sm font-medium transition',
              tab === item.id ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'general' && (
        <Card title="Units">
          <div data-testid="settings-units-card" className="space-y-3">
            <p className="text-xs text-gray-500">
              Affects telemetry pages (Pass Analytics). Existing WPS forms keep their per-field
              unit pickers.
            </p>
            <div className="space-y-2">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="settings-units"
                  value="Imperial"
                  checked={unit === 'Imperial'}
                  onChange={() => setUnit('Imperial')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                Imperial (in, ipm, mil, kJ/in)
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="settings-units"
                  value="Metric"
                  checked={unit === 'Metric'}
                  onChange={() => setUnit('Metric')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                Metric (mm, mm/s, mm, kJ/mm)
              </label>
            </div>
          </div>
        </Card>
      )}

      {tab === 'standards' && (
        <Card title="Standards & Validation Rules" action={<Badge tone="primary">ASME / ISO / DNV</Badge>}>
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <p className="text-sm font-semibold text-gray-900">Active Code Set</p>
              <p className="text-xs text-gray-500">ASME IX + API 1104 (Pipeline)</p>
              <div className="mt-3 space-y-2 text-xs text-gray-500">
                <p>Heat input limits enforced per material group.</p>
                <p>Joint design checks against pipe size categories.</p>
                <p>Auto-flag shielding gas mismatches.</p>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-500">Rule packs</p>
              <div className="mt-2 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">API 1104 Pipeline</span>
                  <Badge tone="success">Enabled</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">ASME IX Section IX</span>
                  <Badge tone="success">Enabled</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">ISO 15614</span>
                  <Badge tone="gray">Off</Badge>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {tab === 'access' && (
        <Card title="Access Control">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-900">Engineer</p>
              <p className="text-xs text-gray-500">Create, clone, and edit WPS drafts.</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-900">Reviewer</p>
              <p className="text-xs text-gray-500">Approve, comment, and sign revisions.</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-900">QA Admin</p>
              <p className="text-xs text-gray-500">Manage standards, numbering, and templates.</p>
            </div>
          </div>
          <Button variant="secondary" className="mt-4">
            Configure Roles
          </Button>
        </Card>
      )}

      {tab === 'catalog' && (
        <Card
        >
          <CatalogAdminPanel />
        </Card>
      )}

      {tab === 'passSequence' && (
        <Card>
          <PassSequenceImagesAdmin />
        </Card>
      )}

      {tab === 'passNameMappings' && <PassNameMappingsAdmin />}

      {tab === 'bevelGeometry' && (
        <Card>
          <BevelGeometryImagesAdmin />
        </Card>
      )}
    </div>
  )
}
